import { useState, useEffect, useCallback } from 'react';
import {
  Leaf,
  Users,
  Shield,
  Plus,
  Loader2,
  AlertCircle,
  Target,
  X,
  TrendingDown,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import clsx from 'clsx';
import { periodDisplayName, type ESGMetric, type ESGDashboardData, type Period } from '@/types';
import { esgApi, periodsApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Category Config ---------------------------------------------------------

// --- Target Status Badge -----------------------------------------------------

function TargetStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; bg: string; text: string; icon: React.ElementType }> = {
    on_track: { label: 'On Track', bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle2 },
    at_risk: { label: 'At Risk', bg: 'bg-amber-50', text: 'text-amber-700', icon: AlertTriangle },
    behind: { label: 'Behind', bg: 'bg-red-50', text: 'text-red-700', icon: TrendingDown },
    achieved: { label: 'Achieved', bg: 'bg-blue-50', text: 'text-blue-700', icon: CheckCircle2 },
  };
  const c = config[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-600', icon: Clock };
  const StatusIcon = c.icon;
  return (
    <span className={clsx('badge gap-1', c.bg, c.text)}>
      <StatusIcon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

// --- Score Card --------------------------------------------------------------

function ScoreCard({
  label,
  score,
  color,
  bg,
  icon: Icon,
}: {
  label: string;
  score: number;
  color: string;
  bg: string;
  icon: React.ElementType;
}) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
        <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center', bg)}>
          <Icon className="w-4.5 h-4.5" style={{ color }} />
        </div>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold" style={{ color }}>{score}</span>
        <span className="text-sm text-gray-500 mb-0.5">/100</span>
      </div>
      <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${score}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

// --- Add Metric Modal --------------------------------------------------------

interface AddMetricModalProps {
  onClose: () => void;
  onSubmit: (data: Partial<ESGMetric>) => Promise<void>;
}

function AddMetricModal({ onClose, onSubmit }: AddMetricModalProps) {
  const [form, setForm] = useState({
    category: 'environmental',
    metric_name: '',
    value: '',
    unit: '',
    framework: 'GRI',
    framework_reference: '',
    entity_id: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        ...form,
        value: parseFloat(form.value) || 0,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Add ESG Metric</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Category</label>
            <select className="select-field text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="environmental">Environmental</option>
              <option value="social">Social</option>
              <option value="governance">Governance</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Metric Name</label>
            <input className="input-field text-sm" value={form.metric_name} onChange={(e) => setForm({ ...form, metric_name: e.target.value })} placeholder="e.g., Carbon Emissions" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Value</label>
              <input type="number" className="input-field text-sm" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Unit</label>
              <input className="input-field text-sm" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g., tCO2e" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Framework</label>
              <select className="select-field text-sm" value={form.framework} onChange={(e) => setForm({ ...form, framework: e.target.value })}>
                <option value="GRI">GRI</option>
                <option value="SASB">SASB</option>
                <option value="TCFD">TCFD</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Reference</label>
              <input className="input-field text-sm" value={form.framework_reference} onChange={(e) => setForm({ ...form, framework_reference: e.target.value })} placeholder="e.g., GRI 305-1" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || !form.metric_name} className="btn-primary text-sm">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add Metric
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---------------------------------------------------------------

export default function ESGDashboard() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<ESGDashboardData | null>(null);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [showAddMetric, setShowAddMetric] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [periodsRes, dashRes] = await Promise.all([
        periodsApi.list(),
        esgApi.dashboard(selectedPeriod ? { period_id: selectedPeriod } : undefined).catch(() => null),
      ]);
      setPeriods(periodsRes);
      if (!selectedPeriod && periodsRes.length > 0) {
        const open = periodsRes.find((p) => p.status === 'open') || periodsRes[0];
        setSelectedPeriod(open.id);
      }
      setDashboardData(dashRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load ESG data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddMetric = async (data: Partial<ESGMetric>) => {
    try {
      await esgApi.createMetric(data);
      addNotification({ type: 'success', title: 'Metric Added', message: 'ESG metric has been recorded.' });
      await loadData();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Add Failed', message: apiErr.detail || 'Failed to add metric.' });
    }
  };

  // Build sample chart data from dashboard
  const envMetrics = dashboardData?.metrics_by_category?.environmental || [];
  const socialMetrics = dashboardData?.metrics_by_category?.social || [];
  const govMetrics = dashboardData?.metrics_by_category?.governance || [];

  // Energy mix pie chart data
  const energyMixData = envMetrics
    .filter((m) => m.metric_name.toLowerCase().includes('energy'))
    .map((m, i) => ({ name: m.metric_name, value: m.value, color: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0'][i % 4] }));

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Leaf className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="skeleton w-16 h-3 mb-3" />
              <div className="skeleton w-12 h-8 mb-2" />
              <div className="skeleton w-full h-2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Leaf className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ESG Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Environmental, Social & Governance reporting</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
          <button onClick={() => setShowAddMetric(true)} className="btn-primary text-sm">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Metric
          </button>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Overall Score</span>
            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
              <Target className="w-4.5 h-4.5 text-gray-600" />
            </div>
          </div>
          <span className="text-3xl font-bold text-gray-900">{dashboardData?.overall_score ?? 0}</span>
          <span className="text-sm text-gray-500 ml-1">/100</span>
        </div>
        <ScoreCard
          label="Environmental"
          score={dashboardData?.environmental_score ?? 0}
          color="#10b981"
          bg="bg-emerald-50"
          icon={Leaf}
        />
        <ScoreCard
          label="Social"
          score={dashboardData?.social_score ?? 0}
          color="#3b82f6"
          bg="bg-blue-50"
          icon={Users}
        />
        <ScoreCard
          label="Governance"
          score={dashboardData?.governance_score ?? 0}
          color="#8b5cf6"
          bg="bg-purple-50"
          icon={Shield}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Carbon Emissions Trend */}
        <div className="card p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Environmental Metrics</h2>
          {envMetrics.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={envMetrics.map((m) => ({ name: m.metric_name, value: m.value }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }} />
                <Area type="monotone" dataKey="value" stroke="#10b981" fill="#d1fae5" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-12 text-center text-sm text-gray-400">No environmental metrics recorded</div>
          )}
        </div>

        {/* Energy Mix Pie */}
        <div className="card p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Energy Mix</h2>
          {energyMixData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={energyMixData} cx="50%" cy="50%" outerRadius={90} innerRadius={50} dataKey="value" paddingAngle={3}>
                  {energyMixData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-12 text-center text-sm text-gray-400">No energy data available</div>
          )}
        </div>
      </div>

      {/* Social & Governance Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Social Metrics */}
        <div className="card p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Social Metrics</h2>
          {socialMetrics.length > 0 ? (
            <div className="space-y-3">
              {socialMetrics.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{m.metric_name}</p>
                    <p className="text-xs text-gray-500">{m.framework} {m.framework_reference}</p>
                  </div>
                  <span className="text-lg font-bold text-blue-700 font-mono">{m.value} <span className="text-xs text-gray-500 font-normal">{m.unit}</span></span>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-gray-400">No social metrics recorded</div>
          )}
        </div>

        {/* Governance Metrics */}
        <div className="card p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Governance Metrics</h2>
          {govMetrics.length > 0 ? (
            <div className="space-y-3">
              {govMetrics.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-3 bg-purple-50/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{m.metric_name}</p>
                    <p className="text-xs text-gray-500">{m.framework} {m.framework_reference}</p>
                  </div>
                  <span className="text-lg font-bold text-purple-700 font-mono">{m.value} <span className="text-xs text-gray-500 font-normal">{m.unit}</span></span>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-gray-400">No governance metrics recorded</div>
          )}
        </div>
      </div>

      {/* Framework Coverage */}
      {dashboardData?.framework_coverage && dashboardData.framework_coverage.length > 0 && (
        <div className="card p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Framework Coverage</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dashboardData.framework_coverage.map((fc) => (
              <div key={fc.framework} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">{fc.framework}</span>
                  <span className="text-sm font-bold text-gray-900">{Math.round(fc.coverage_pct)}%</span>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={clsx(
                      'h-full rounded-full transition-all',
                      fc.coverage_pct >= 80 ? 'bg-green-500' : fc.coverage_pct >= 50 ? 'bg-amber-500' : 'bg-red-500',
                    )}
                    style={{ width: `${fc.coverage_pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Target Tracking */}
      {dashboardData?.targets && dashboardData.targets.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
            <h2 className="text-base font-semibold text-gray-900">Target Tracking</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="table-header">Metric</th>
                  <th className="table-header text-right">Baseline</th>
                  <th className="table-header text-right">Current</th>
                  <th className="table-header text-right">Target</th>
                  <th className="table-header">Target Year</th>
                  <th className="table-header">Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.targets.map((target) => (
                  <tr key={target.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <td className="table-cell text-sm font-medium text-gray-900">{target.metric_name || target.metric_code}</td>
                    <td className="table-cell text-right font-mono text-sm text-gray-600">{target.baseline_value.toLocaleString()}</td>
                    <td className="table-cell text-right font-mono text-sm text-gray-900 font-medium">{(target.current_value ?? 0).toLocaleString()}</td>
                    <td className="table-cell text-right font-mono text-sm text-gray-600">{target.target_value.toLocaleString()}</td>
                    <td className="table-cell text-sm text-gray-600">{target.target_year}</td>
                    <td className="table-cell"><TargetStatusBadge status={target.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Metric Modal */}
      {showAddMetric && (
        <AddMetricModal onClose={() => setShowAddMetric(false)} onSubmit={handleAddMetric} />
      )}
    </div>
  );
}
